import { uidUrl } from "src/common/api/uidUrl";
import { apiAdminUrl, apiObjUrl } from "src/common/apiUrl";
import {
  edValue2EntityField,
  entityField2EdValue,
} from "src/common/attrEdit/transforms";
import { AttributeValue } from "src/common/attributes/valueTypes/AttributeValue";
import { rest } from "src/common/rest";
import { restWithObjectVersion } from "src/common/restWithObjectVersion";
import { ZAttribute } from "src/types/ZAttribute";
import {
  ZEntity,
  zEntity,
  ZEntityField,
  zEntityField,
} from "src/types/ZEntity";
import { ZUid } from "src/types/zUid";
import {
  ZEntityAvailableState,
  zEntityAvailableState,
} from "./EntityPage.types";

// Поля формы в виде ассоциативного массива String(attributeId) => value
export type EdCardValues = Record<ZUid, AttributeValue>;

export const makeFieldName = (attrId: ZUid) => String(attrId);

export const ed2entity = (
  ed: EdCardValues,
  entityId: ZUid,
  objectId: ZUid,
  attributesMap: Record<ZUid, ZAttribute>,
): ZEntity => ({
  id: entityId,
  objectId,
  // ВНИМАНИЕ: undefined здесь означает "атрибут не трогать" - он выпадает из
  // запроса, и бэк сохранит старое значение. Очистка значения кодируется
  // пустым массивом []. Атрибуты, отсутствующие в attributesMap (карта
  // строится из all-attributes), отбрасываются молча. LPLM-5173
  attributeValues: Object.entries(ed)
    .filter(([key, value]) => value !== undefined && !!attributesMap[key])
    .map(([key, value]) => {
      const attr = attributesMap[key];
      if (!attr) {
        throw new Error("ed2entity: wrong attribute");
      }
      return edValue2EntityField(attr, value);
    }),
});

/**
 * Форматом для хранения значения поля на сервере является string[]
 * Раньше была попытка конвертировать в формат соответствующего компонента.
 * Но для этого нужно знать описание атрибута. А например атрибуты внутри зависимых групп подгружаются уже после создания формы.
 * Поэтому теперь форма хранит данные в таком же формате, как и сервер.
 * Но для каждого компонента нужен proxy, который конвертирует формат на лету. Они собраны в fieldComponents
 * @param entity
 * @returns
 */
export const entity2ed = (
  entity: ZEntity,
  attributesMap: Record<ZUid, ZAttribute>,
): EdCardValues =>
  entity.attributeValues.reduce((acc, valueField) => {
    const attr = attributesMap[valueField.attributeId];
    if (!attr) return acc;
    return {
      ...acc,
      [makeFieldName(valueField.attributeId)]: entityField2EdValue(
        attr,
        valueField,
      ),
    };
  }, {});

export const createEntity = async (entity: ZEntity): Promise<ZEntity> => {
  const { id, ...other } = entity;
  const resp = await rest.post(apiObjUrl(uidUrl("/entities")), other);
  return zEntity.parse(resp.data);
};

export const createEntitiesByIterator = async (
  entity: ZEntity,
): Promise<ZEntity[]> => {
  const { id, ...other } = entity;
  const resp = await rest.post(apiObjUrl(uidUrl("/entities/iterator")), other);
  return zEntity.array().parse(resp.data);
};

export const loadEntity = async (
  entityId: ZUid,
  options: {
    translate?: boolean;
  } = {},
): Promise<ZEntity> => {
  const resp = await rest.get(apiObjUrl(`${uidUrl("/entities")}/${entityId}`), {
    translate: options.translate,
    params: { withLabels: true },
  });
  return zEntity.parse(resp.data);
};

export const saveEntityPatch = async (entity: ZEntity): Promise<ZEntity> => {
  const resp = await rest.put(apiObjUrl(uidUrl("/entities/partial")), entity, {
    params: { withLabels: true },
  });
  return zEntity.parse(resp.data);
};
export const calculateEntityFormulas = async (
  entity: ZEntity,
): Promise<ZEntityField[]> => {
  const resp = await rest.post(apiObjUrl(`/formulas/calculate`), entity);
  return zEntityField.array().parse(resp.data);
};

export const validateEntityFormula = async (formula: string, objectId: ZUid) =>
  rest.get(apiObjUrl(`/formulas/validate`), {
    params: { formula, objectId },
  });

export const deleteEntity = async (entityId: ZUid) => {
  await rest.delete(apiObjUrl(`${uidUrl("/entities")}/${entityId}`));
};

export const loadAvailableStates = async (
  objectId: ZUid,
  stateId: ZUid,
  options: {
    translate?: boolean;
  } = {},
): Promise<ZEntityAvailableState[]> => {
  const resp = await restWithObjectVersion.get(
    apiAdminUrl("/states/available"),
    {
      params: { objectId, stateId },
      translate: options.translate,
    },
  );
  return zEntityAvailableState.array().parse(resp.data);
};

export const callChangeEntityState = async (entityId: ZUid, stateId: ZUid) => {
  await rest.put(apiObjUrl(`${uidUrl("/entities")}/${entityId}/state`), {
    stateId,
  });
};

export const changeEntitiesState = async (stateId: ZUid, entities: ZUid[]) => {
  await rest.put(apiObjUrl("/entities/update-states"), { stateId, entities });
};
